import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import { BehaviorSubject, Observable, Subscription, combineLatest, timer } from 'rxjs';
import { IUserDeviceCommand, IUserDeviceCommandRequestedFeedback, IUserDeviceCommandRetryPolicy } from '@platfio/interfaces/platfio';
import { NativeDeviceCommandEvent } from '../interfaces/native-host';
import { NativeHostService } from './native-host.service';
import { UserDeviceService } from './user-device.service';

interface ActiveDesktopCommand {
  attemptRecords: Array<{
    attempt: number;
    completedAt: string;
    error?: string;
    exitCode?: number | null;
    retryable: boolean;
    startedAt: string;
    status: 'completed' | 'failed';
    summary: string;
  }>;
  command: IUserDeviceCommand;
  currentAttempt: number;
  currentAttemptOutput: string;
  deviceId: string;
  output: string;
  retryPolicy: Required<IUserDeviceCommandRetryPolicy>;
  uid: string;
}

@Injectable({
  providedIn: 'root'
})
export class DesktopDeviceCommandRunnerService implements OnDestroy {
  private readonly isBrowser: boolean;
  private readonly activeCommands = new Map<string, ActiveDesktopCommand>();
  private readonly liveOutputSubjects = new Map<string, BehaviorSubject<string>>();
  private commandSubscription?: Subscription;
  private contextSubscription?: Subscription;
  private desktopHeartbeatSubscription?: Subscription;
  private deviceEventSubscription?: Subscription;
  private currentContext: { deviceId: string; uid: string } | null = null;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private afAuth: AngularFireAuth,
    private nativeHostService: NativeHostService,
    private userDeviceService: UserDeviceService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (!this.isBrowser) {
      return;
    }

    this.deviceEventSubscription = this.nativeHostService.deviceCommandEvent$.subscribe((event) => {
      void this.handleNativeDeviceEvent(event);
    });

    this.contextSubscription = combineLatest([
      this.afAuth.authState,
      this.nativeHostService.desktopAvailable$
    ]).subscribe(([user, desktopAvailable]) => {
      void this.syncDesktopContext(user?.uid || null, desktopAvailable);
    });
  }

  ensureStarted(): void {
    // Intentionally empty. Injecting the service in AppComponent is enough to start the subscriptions.
  }

  ngOnDestroy(): void {
    this.contextSubscription?.unsubscribe();
    this.commandSubscription?.unsubscribe();
    this.desktopHeartbeatSubscription?.unsubscribe();
    this.deviceEventSubscription?.unsubscribe();
  }

  getLiveOutput(commandId: string): Observable<string> {
    return this.ensureLiveOutputSubject(commandId).asObservable();
  }

  private async syncDesktopContext(uid: string | null, desktopAvailable: boolean): Promise<void> {
    const deviceId = desktopAvailable ? this.getDesktopDeviceId() : null;

    if (!uid || !deviceId) {
      this.clearDesktopSubscriptions();
      this.currentContext = null;
      return;
    }

    if (this.currentContext?.uid === uid && this.currentContext.deviceId === deviceId) {
      return;
    }

    this.clearDesktopSubscriptions();
    this.currentContext = { uid, deviceId };

    this.desktopHeartbeatSubscription = timer(0, 60_000).subscribe(() => {
      void this.userDeviceService.touchDeviceHeartbeat(uid, deviceId);
    });

    this.commandSubscription = this.userDeviceService.getDeviceCommands(uid, deviceId).subscribe({
      next: (commands) => {
        for (const command of commands) {
          if (command.commandType === 'terminal' && command.status === 'pending' && !this.activeCommands.has(command.commandId)) {
            void this.executeCommand(uid, deviceId, command);
          }
        }
      },
      error: (error) => {
        console.error('Desktop device command subscription failed:', error);
      }
    });
  }

  private clearDesktopSubscriptions(): void {
    this.commandSubscription?.unsubscribe();
    this.commandSubscription = undefined;
    this.desktopHeartbeatSubscription?.unsubscribe();
    this.desktopHeartbeatSubscription = undefined;
  }

  private async executeCommand(uid: string, deviceId: string, command: IUserDeviceCommand): Promise<void> {
    this.activeCommands.set(command.commandId, {
      attemptRecords: [],
      uid,
      deviceId,
      command,
      currentAttempt: 0,
      currentAttemptOutput: '',
      retryPolicy: this.resolveRetryPolicy(command.retryPolicy),
      output: command.feedback?.terminal?.stdout || ''
    });

    this.ensureLiveOutputSubject(command.commandId).next(command.feedback?.terminal?.stdout || '');

    await this.startAttempt(command.commandId);
  }

  private async startAttempt(commandId: string): Promise<void> {
    const activeCommand = this.activeCommands.get(commandId);
    if (!activeCommand) {
      return;
    }

    activeCommand.currentAttempt += 1;
    activeCommand.currentAttemptOutput = '';

    await this.userDeviceService.updateDeviceCommand(activeCommand.uid, activeCommand.deviceId, activeCommand.command.commandId, {
      status: 'running',
      startedAt: firebase.firestore.FieldValue.serverTimestamp(),
      result: `Running terminal command attempt ${activeCommand.currentAttempt}/${activeCommand.retryPolicy.maxAttempts}: ${activeCommand.command.command}`,
      feedback: {
        summary: `Running terminal command attempt ${activeCommand.currentAttempt}/${activeCommand.retryPolicy.maxAttempts}: ${activeCommand.command.command}`,
        terminal: {
          exitCode: null,
          stderr: null,
          stdout: activeCommand.output
        },
        metadata: {
          execution: {
            attemptCount: activeCommand.currentAttempt,
            attempts: activeCommand.attemptRecords,
            lastAttemptStatus: 'running',
            maxAttempts: activeCommand.retryPolicy.maxAttempts,
            retryable: activeCommand.retryPolicy.strategy !== 'never',
            strategy: activeCommand.retryPolicy.strategy,
            willRetry: false
          },
          runner: 'frontend-native-host'
        }
      },
      error: null
    });

    const executionResult = await this.nativeHostService.executeDeviceCommand({
      command: activeCommand.command.command,
      commandId: activeCommand.command.commandId
    });

    if (!executionResult.ok) {
      await this.finalizeCommand(activeCommand.command.commandId, {
        commandId: activeCommand.command.commandId,
        message: executionResult.message || 'The native desktop host could not start this terminal command.',
        type: 'error'
      });
    }
  }

  private async handleNativeDeviceEvent(event: NativeDeviceCommandEvent): Promise<void> {
    if (!this.activeCommands.has(event.commandId)) {
      return;
    }

    if (event.type === 'output') {
      const activeCommand = this.activeCommands.get(event.commandId);
      if (!activeCommand) {
        return;
      }

      activeCommand.output += event.chunk || '';
      activeCommand.currentAttemptOutput += event.chunk || '';
      this.ensureLiveOutputSubject(event.commandId).next(activeCommand.output);
      return;
    }

    if (event.type === 'completed' || event.type === 'error') {
      await this.finalizeCommand(event.commandId, event);
    }
  }

  private async finalizeCommand(commandId: string, event: NativeDeviceCommandEvent): Promise<void> {
    const activeCommand = this.activeCommands.get(commandId);
    if (!activeCommand) {
      return;
    }

    const requestedFeedback = activeCommand.command.requestedFeedback;
    const includeTerminalOutput = requestedFeedback?.includeTerminalOutput !== false;
    const includeExitCode = requestedFeedback?.includeExitCode !== false;
    const includeSummary = requestedFeedback?.includeSummary !== false;
    const outputMaxCharacters = this.resolveOutputLimit(requestedFeedback);
    const fullOutput = activeCommand.output;
    const stdout = includeTerminalOutput ? truncateFromEnd(fullOutput, outputMaxCharacters) : null;
    const exitCode = typeof event.exitCode === 'number' ? event.exitCode : null;
    const succeeded = event.type === 'completed' && (exitCode === null || exitCode === 0);
    const failureMessage = event.message || this.buildFailureSummary(exitCode);
    const classification = this.classifyDesktopFailure(event, exitCode);
    const willRetry = !succeeded && this.shouldRetry(activeCommand.retryPolicy, activeCommand.currentAttempt, classification.retryable);
    const summary = includeSummary
      ? succeeded
        ? this.buildSuccessSummary(activeCommand.command.command, exitCode)
        : failureMessage
      : null;

    activeCommand.attemptRecords.push({
      attempt: activeCommand.currentAttempt,
      completedAt: new Date().toISOString(),
      ...(succeeded ? {} : { error: failureMessage }),
      ...(exitCode !== null ? { exitCode } : {}),
      retryable: classification.retryable,
      startedAt: new Date().toISOString(),
      status: succeeded ? 'completed' : 'failed',
      summary: summary || (succeeded ? 'Completed terminal command.' : failureMessage)
    });

    this.ensureLiveOutputSubject(commandId).next(fullOutput);

    if (willRetry) {
      await this.userDeviceService.updateDeviceCommand(activeCommand.uid, activeCommand.deviceId, commandId, {
        status: 'running',
        result: `Retrying terminal command after attempt ${activeCommand.currentAttempt}/${activeCommand.retryPolicy.maxAttempts}.`,
        feedback: {
          summary: `Terminal command failed on attempt ${activeCommand.currentAttempt}/${activeCommand.retryPolicy.maxAttempts}. Retrying shortly. ${failureMessage}`,
          terminal: {
            exitCode: includeExitCode ? exitCode : null,
            stderr: failureMessage,
            stdout
          },
          metadata: {
            execution: {
              attemptCount: activeCommand.currentAttempt,
              attempts: activeCommand.attemptRecords,
              lastAttemptStatus: 'failed',
              lastError: failureMessage,
              maxAttempts: activeCommand.retryPolicy.maxAttempts,
              retryable: classification.retryable,
              strategy: activeCommand.retryPolicy.strategy,
              willRetry: true
            },
            outputTruncated: includeTerminalOutput ? fullOutput.length > outputMaxCharacters : false,
            runner: 'frontend-native-host',
            totalOutputCharacters: fullOutput.length
          }
        },
        error: null
      });

      if (activeCommand.retryPolicy.retryDelayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, activeCommand.retryPolicy.retryDelayMs));
      }

      await this.startAttempt(commandId);
      return;
    }

    await this.userDeviceService.updateDeviceCommand(activeCommand.uid, activeCommand.deviceId, commandId, {
      status: succeeded ? 'completed' : 'failed',
      completedAt: firebase.firestore.FieldValue.serverTimestamp(),
      result: summary,
      feedback: {
        summary,
        terminal: {
          exitCode: includeExitCode ? exitCode : null,
          stderr: succeeded ? null : failureMessage,
          stdout
        },
        metadata: {
          execution: {
            attemptCount: activeCommand.currentAttempt,
            attempts: activeCommand.attemptRecords,
            lastAttemptStatus: succeeded ? 'completed' : 'failed',
            ...(succeeded ? {} : { lastError: failureMessage }),
            maxAttempts: activeCommand.retryPolicy.maxAttempts,
            retryable: classification.retryable,
            strategy: activeCommand.retryPolicy.strategy,
            willRetry: false
          },
          outputTruncated: includeTerminalOutput ? fullOutput.length > outputMaxCharacters : false,
          runner: 'frontend-native-host',
          totalOutputCharacters: fullOutput.length
        }
      },
      error: succeeded ? null : failureMessage
    });

    this.activeCommands.delete(commandId);
  }

  private buildSuccessSummary(command: string, exitCode: number | null): string {
    if (exitCode === null) {
      return `Completed terminal command: ${command}`;
    }

    return `Completed terminal command with exit code ${exitCode}: ${command}`;
  }

  private buildFailureSummary(exitCode: number | null): string {
    if (exitCode === null) {
      return 'Terminal command failed.';
    }

    return `Terminal command failed with exit code ${exitCode}.`;
  }

  private ensureLiveOutputSubject(commandId: string): BehaviorSubject<string> {
    const existingSubject = this.liveOutputSubjects.get(commandId);
    if (existingSubject) {
      return existingSubject;
    }

    const subject = new BehaviorSubject<string>('');
    this.liveOutputSubjects.set(commandId, subject);
    return subject;
  }

  private getDesktopDeviceId(): string | null {
    if (!this.isBrowser || typeof window === 'undefined') {
      return null;
    }

    return window.localStorage.getItem('platfio-desktop-device-id');
  }

  private resolveOutputLimit(requestedFeedback?: IUserDeviceCommandRequestedFeedback | null): number {
    const requestedLimit = requestedFeedback?.outputMaxCharacters;
    if (typeof requestedLimit === 'number' && requestedLimit > 0) {
      return Math.floor(requestedLimit);
    }

    return 12_000;
  }

  private resolveRetryPolicy(retryPolicy?: IUserDeviceCommandRetryPolicy | null): Required<IUserDeviceCommandRetryPolicy> {
    return {
      maxAttempts: typeof retryPolicy?.maxAttempts === 'number' && retryPolicy.maxAttempts > 0 ? Math.floor(retryPolicy.maxAttempts) : 2,
      retryDelayMs: typeof retryPolicy?.retryDelayMs === 'number' && retryPolicy.retryDelayMs >= 0 ? Math.floor(retryPolicy.retryDelayMs) : 0,
      strategy: retryPolicy?.strategy || 'transient-only'
    };
  }

  private classifyDesktopFailure(event: NativeDeviceCommandEvent, exitCode: number | null): { retryable: boolean } {
    const message = (event.message || '').toLowerCase();
    if (event.type === 'error') {
      return {
        retryable: /spawn|enoent|eagain|timed out|native desktop host/.test(message)
      };
    }

    return {
      retryable: exitCode === null
    };
  }

  private shouldRetry(
    retryPolicy: Required<IUserDeviceCommandRetryPolicy>,
    attempt: number,
    retryable: boolean
  ): boolean {
    if (retryPolicy.strategy === 'never' || attempt >= retryPolicy.maxAttempts) {
      return false;
    }

    if (retryPolicy.strategy === 'always') {
      return true;
    }

    return retryable;
  }
}

function truncateFromEnd(value: string, limit: number): string {
  if (value.length <= limit) {
    return value;
  }

  return value.slice(value.length - limit);
}